import { useEffect, useRef } from 'react';
import { EditorState, RangeSetBuilder, StateField } from '@codemirror/state';
import { EditorView, Decoration, lineNumbers } from '@codemirror/view';
import { json as jsonLang } from '@codemirror/lang-json';
import { syntaxHighlighting, defaultHighlightStyle } from '@codemirror/language';

/**
 * Read-only JSON viewer that highlights specific lines (0-based indices). Used by the Configuration
 * Store "Preview" dialog to show the merged Base + override result with the override's contributed
 * lines highlighted, so an editor can see exactly what the layer changes/adds on top of Base.
 */
export function MergedPreviewView({
  text,
  highlightLines,
  height = '55vh',
}: {
  text: string;
  highlightLines: number[];
  height?: string;
}) {
  const hostRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!hostRef.current) return;
    const hl = new Set(highlightLines);
    const lineDeco = Decoration.line({ class: 'cm-override-line' });

    const build = (state: EditorState) => {
      const b = new RangeSetBuilder<Decoration>();
      for (let i = 1; i <= state.doc.lines; i++) {
        if (hl.has(i - 1)) {
          const line = state.doc.line(i);
          b.add(line.from, line.from, lineDeco);
        }
      }
      return b.finish();
    };

    const hlField = StateField.define({
      create: (state) => build(state),
      update: (deco) => deco, // read-only doc, no remapping needed
      provide: (f) => EditorView.decorations.from(f),
    });

    const view = new EditorView({
      parent: hostRef.current,
      state: EditorState.create({
        doc: text,
        extensions: [
          lineNumbers(),
          jsonLang(),
          syntaxHighlighting(defaultHighlightStyle),
          EditorView.editable.of(false),
          EditorState.readOnly.of(true),
          EditorView.lineWrapping,
          hlField,
          EditorView.theme({
            '.cm-override-line': { backgroundColor: 'rgba(45, 164, 78, 0.14)' },
          }),
        ],
      }),
    });
    return () => view.destroy();
  }, [text, highlightLines]);

  return <div ref={hostRef} style={{ height, overflow: 'auto' }} />;
}
